'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { BLOG_TAB_KEYS, type BlogData, type BlogTabKey, type BlogVideo, getBlogImageUrl } from '@/entities/blog';
import { resolvePublicHref } from '@/shared/lib/public-routes';
import { DEFAULT_LANGUAGE } from '@/shared/localization';
import { RevealOnScroll } from '@/shared/ui/reveal-on-scroll';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import styles from './BlogHome.module.scss';
const POSTS_PER_PAGE = 5;
const VIDEO_THUMB_GRADIENTS = [styles.videoThumbGradient0, styles.videoThumbGradient1, styles.videoThumbGradient2, styles.videoThumbGradient3, styles.videoThumbGradient4, styles.videoThumbGradient5];
type LocaleParams = {
  locale: string;
};
function joinClassNames(...names: Array<string | false | null | undefined>): string {
  return names.filter(Boolean).join(' ');
}
function nextVideoIndex(current: number, count: number): number {
  return Math.min(count - 1, current + 1);
}
function previousVideoIndex(current: number): number {
  return Math.max(0, current - 1);
}
export type BlogHomeProps = {
  // 현재 locale 의 블로그 데이터. 서버 라우트에서 getBlogData(locale) 로 조회해
  // 주입한다 — 클라이언트에서 직접 import 하면 ko/en 양쪽 데이터가 번들에 실림.
  data: BlogData;
};
export function BlogHome(t0) {
  const $ = _c(143);
  const {
    data
  } = t0;
  const params = useParams();
  const locale = params?.locale ?? DEFAULT_LANGUAGE;
  const t = useTranslations("blog");
  const {
    videos,
    posts
  } = data;
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [videoIdx, setVideoIdx] = useState(null);
  let t1;
  if ($[0] !== activeTab || $[1] !== posts || $[2] !== search) {
    const keyword = search.trim().toLowerCase();
    t1 = posts.filter(post => {
      const categoryMatch = activeTab === "all" || post.categoryKey === activeTab;
      const searchMatch = keyword.length === 0 || post.title.toLowerCase().includes(keyword) || post.description.toLowerCase().includes(keyword);
      return categoryMatch && searchMatch;
    });
    $[0] = activeTab;
    $[1] = posts;
    $[2] = search;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const filteredPosts = t1;
  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  let handlePageNext;
  let handlePagePrev;
  let handleVideoClose;
  let handleVideoNext;
  let handleVideoPrev;
  let pageNumbers;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[4] !== activeTab || $[5] !== currentPage || $[6] !== filteredPosts || $[7] !== locale || $[8] !== posts.length || $[9] !== search || $[10] !== t || $[11] !== totalPages || $[12] !== videos) {
    const visiblePosts = filteredPosts.slice((currentPage - 1) * POSTS_PER_PAGE, currentPage * POSTS_PER_PAGE);
    let t10;
    if ($[27] !== totalPages) {
      t10 = Array.from({
        length: totalPages
      }, _temp).slice(0, 5);
      $[27] = totalPages;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    pageNumbers = t10;
    let t11;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = event => {
        setSearch(event.target.value);
        setPage(1);
      };
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    const handleSearchChange = t11;
    let t12;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = tab => {
        setActiveTab(tab);
        setPage(1);
      };
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    const handleTabSelect = t12;
    let t13;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = () => {
        setPage(_temp2);
      };
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    handlePagePrev = t13;
    let t14;
    if ($[32] !== totalPages) {
      t14 = () => {
        setPage(current_0 => Math.min(totalPages, current_0 + 1));
      };
      $[32] = totalPages;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    handlePageNext = t14;
    let t15;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = index_0 => {
        setVideoIdx(index_0);
      };
      $[34] = t15;
    } else {
      t15 = $[34];
    }
    const handleVideoOpen = t15;
    let t16;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = () => {
        setVideoIdx(null);
      };
      $[35] = t16;
    } else {
      t16 = $[35];
    }
    handleVideoClose = t16;
    let t17;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = () => {
        setVideoIdx(_temp3);
      };
      $[36] = t17;
    } else {
      t17 = $[36];
    }
    handleVideoPrev = t17;
    let t18;
    if ($[37] !== videos.length) {
      t18 = () => {
        setVideoIdx(current_2 => current_2 === null ? current_2 : nextVideoIndex(current_2, videos.length));
      };
      $[37] = videos.length;
      $[38] = t18;
    } else {
      t18 = $[38];
    }
    handleVideoNext = t18;
    t7 = styles.root;
    let t19;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <RevealOnScroll><div className={styles.heroBadge}>VAETKI Commerce Blog</div></RevealOnScroll>;
      $[39] = t19;
    } else {
      t19 = $[39];
    }
    let t20;
    if ($[40] !== t) {
      t20 = t.rich("home.hero.title", {
        br: _temp4,
        hl: _temp5
      });
      $[40] = t;
      $[41] = t20;
    } else {
      t20 = $[41];
    }
    let t21;
    if ($[42] !== t20) {
      t21 = <RevealOnScroll delayMs={120} y={22}><h1 className={styles.heroTitle}>{t20}</h1></RevealOnScroll>;
      $[42] = t20;
      $[43] = t21;
    } else {
      t21 = $[43];
    }
    let t22;
    if ($[44] !== t) {
      t22 = t("home.hero.lead");
      $[44] = t;
      $[45] = t22;
    } else {
      t22 = $[45];
    }
    let t23;
    if ($[46] !== t22) {
      t23 = <RevealOnScroll delayMs={220} y={18}><p className={styles.heroLead}>{t22}</p></RevealOnScroll>;
      $[46] = t22;
      $[47] = t23;
    } else {
      t23 = $[47];
    }
    if ($[48] !== t21 || $[49] !== t23) {
      t8 = <section className={styles.hero}><div className={styles.heroInner}>{t19}{t21}{t23}</div></section>;
      $[48] = t21;
      $[49] = t23;
      $[50] = t8;
    } else {
      t8 = $[50];
    }
    let t24;
    if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = joinClassNames(styles.section, styles.sectionVideos);
      $[51] = t24;
    } else {
      t24 = $[51];
    }
    let t25;
    if ($[52] !== t) {
      t25 = t("home.videos.title");
      $[52] = t;
      $[53] = t25;
    } else {
      t25 = $[53];
    }
    let t26;
    if ($[54] !== t25) {
      t26 = <h2 className={styles.sectionTitle}>{t25}</h2>;
      $[54] = t25;
      $[55] = t26;
    } else {
      t26 = $[55];
    }
    let t27;
    if ($[56] !== t) {
      t27 = t("home.videos.subtitle");
      $[56] = t;
      $[57] = t27;
    } else {
      t27 = $[57];
    }
    let t28;
    if ($[58] !== t27) {
      t28 = <p className={styles.sectionSubtitle}>{t27}</p>;
      $[58] = t27;
      $[59] = t28;
    } else {
      t28 = $[59];
    }
    let t29;
    if ($[60] !== t26 || $[61] !== t28) {
      t29 = <RevealOnScroll><div className={styles.sectionHeader}><div>{t26}{t28}</div></div></RevealOnScroll>;
      $[60] = t26;
      $[61] = t28;
      $[62] = t29;
    } else {
      t29 = $[62];
    }
    let t30;
    if ($[63] !== videos) {
      let t31;
      if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
        t31 = (video, index_1) => {
          const handleClick = () => handleVideoOpen(index_1);
          const gradientClass = VIDEO_THUMB_GRADIENTS[index_1 % VIDEO_THUMB_GRADIENTS.length];
          const videoTitle = video.title;
          return <RevealOnScroll key={video.key} delayMs={index_1 * 70} y={16}><button type="button" onClick={handleClick} className={styles.videoCard}><div className={joinClassNames(styles.videoThumb, gradientClass)}>{video.thumbnail || video.youtubeId ? <img src={video.thumbnail ? getBlogImageUrl(video.thumbnail) : `https://i.ytimg.com/vi/${video.youtubeId}/hqdefault.jpg`} alt={videoTitle} className={styles.videoThumbImg} loading={index_1 === 0 ? "eager" : "lazy"} fetchPriority={index_1 === 0 ? "high" : "auto"} decoding="async" /> : null}<div className={styles.videoDuration}>{video.duration}</div><div className={styles.videoPlay}><div className={styles.videoPlayTriangle} /></div></div><div className={styles.videoMeta}><p className={styles.videoCategory}>{video.category}</p><p className={styles.videoTitle}>{videoTitle}</p></div></button></RevealOnScroll>;
        };
        $[65] = t31;
      } else {
        t31 = $[65];
      }
      t30 = videos.map(t31);
      $[63] = videos;
      $[64] = t30;
    } else {
      t30 = $[64];
    }
    let t31;
    if ($[66] !== t30) {
      t31 = <div className={styles.videoGrid}>{t30}</div>;
      $[66] = t30;
      $[67] = t31;
    } else {
      t31 = $[67];
    }
    if ($[68] !== t29 || $[69] !== t31) {
      t9 = <section className={t24}>{t29}{t31}</section>;
      $[68] = t29;
      $[69] = t31;
      $[70] = t9;
    } else {
      t9 = $[70];
    }
    if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = joinClassNames(styles.section, styles.sectionPosts);
      $[71] = t4;
    } else {
      t4 = $[71];
    }
    let t32;
    if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = joinClassNames(styles.sectionHeader, styles.sectionHeaderTight);
      $[72] = t32;
    } else {
      t32 = $[72];
    }
    let t33;
    if ($[73] !== t) {
      t33 = t("home.posts.title");
      $[73] = t;
      $[74] = t33;
    } else {
      t33 = $[74];
    }
    let t34;
    if ($[75] !== t33) {
      t34 = <h2 className={styles.sectionTitle}>{t33}</h2>;
      $[75] = t33;
      $[76] = t34;
    } else {
      t34 = $[76];
    }
    let t35;
    if ($[77] !== posts.length || $[78] !== t) {
      t35 = t("home.posts.count", {
        count: posts.length
      });
      $[77] = posts.length;
      $[78] = t;
      $[79] = t35;
    } else {
      t35 = $[79];
    }
    let t36;
    if ($[80] !== t35) {
      t36 = <p className={styles.sectionSubtitle}>{t35}</p>;
      $[80] = t35;
      $[81] = t36;
    } else {
      t36 = $[81];
    }
    let t37;
    if ($[82] !== t34 || $[83] !== t36) {
      t37 = <div>{t34}{t36}</div>;
      $[82] = t34;
      $[83] = t36;
      $[84] = t37;
    } else {
      t37 = $[84];
    }
    let t38;
    if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
      t38 = <svg viewBox="0 0 24 24" className={styles.searchIcon} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>;
      $[85] = t38;
    } else {
      t38 = $[85];
    }
    let t39;
    if ($[86] !== t) {
      t39 = t("home.search.placeholder");
      $[86] = t;
      $[87] = t39;
    } else {
      t39 = $[87];
    }
    let t40;
    if ($[88] !== search || $[89] !== t39) {
      t40 = <label className={styles.searchLabel}>{t38}<input value={search} onChange={handleSearchChange} placeholder={t39} className={styles.searchInput} /></label>;
      $[88] = search;
      $[89] = t39;
      $[90] = t40;
    } else {
      t40 = $[90];
    }
    if ($[91] !== t37 || $[92] !== t40) {
      t5 = <RevealOnScroll><div className={t32}>{t37}{t40}</div></RevealOnScroll>;
      $[91] = t37;
      $[92] = t40;
      $[93] = t5;
    } else {
      t5 = $[93];
    }
    let t41;
    if ($[94] !== activeTab || $[95] !== t) {
      t41 = BLOG_TAB_KEYS.map(tab_0 => {
        const isActive = tab_0 === activeTab;
        const handleClick_0 = () => handleTabSelect(tab_0);
        return <button key={tab_0} type="button" onClick={handleClick_0} className={joinClassNames(styles.tabButton, isActive && styles.tabButtonActive)}>{t(`tabs.${tab_0}`)}</button>;
      });
      $[94] = activeTab;
      $[95] = t;
      $[96] = t41;
    } else {
      t41 = $[96];
    }
    if ($[97] !== t41) {
      t6 = <RevealOnScroll delayMs={80}><div className={styles.tabRow}>{t41}</div></RevealOnScroll>;
      $[97] = t41;
      $[98] = t6;
    } else {
      t6 = $[98];
    }
    t2 = styles.postList;
    let t42;
    if ($[99] !== locale || $[100] !== t) {
      t42 = (post_0, index_2) => <RevealOnScroll key={post_0.id} delayMs={index_2 * 70} y={16}><Link href={resolvePublicHref(locale, `blog/${post_0.slug}`) ?? "#"} className={styles.postCard}><div className={styles.postBody}><div className={styles.postCategory}>{t(`tabs.${post_0.categoryKey}`)}</div><div className={styles.postMeta}><span>{post_0.date}</span></div><h3 className={styles.postTitle}>{post_0.title}</h3><p className={styles.postDescription}>{post_0.description}</p></div><div className={styles.postArrow}><svg viewBox="0 0 24 24" className={styles.postArrowIcon} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg></div></Link></RevealOnScroll>;
      $[99] = locale;
      $[100] = t;
      $[101] = t42;
    } else {
      t42 = $[101];
    }
    t3 = visiblePosts.map(t42);
    $[4] = activeTab;
    $[5] = currentPage;
    $[6] = filteredPosts;
    $[7] = locale;
    $[8] = posts.length;
    $[9] = search;
    $[10] = t;
    $[11] = totalPages;
    $[12] = videos;
    $[13] = handlePageNext;
    $[14] = handlePagePrev;
    $[15] = handleVideoClose;
    $[16] = handleVideoNext;
    $[17] = handleVideoPrev;
    $[18] = pageNumbers;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    handlePageNext = $[13];
    handlePagePrev = $[14];
    handleVideoClose = $[15];
    handleVideoNext = $[16];
    handleVideoPrev = $[17];
    pageNumbers = $[18];
    t2 = $[19];
    t3 = $[20];
    t4 = $[21];
    t5 = $[22];
    t6 = $[23];
    t7 = $[24];
    t8 = $[25];
    t9 = $[26];
  }
  let t10;
  if ($[102] !== t2 || $[103] !== t3) {
    t10 = <div className={t2}>{t3}</div>;
    $[102] = t2;
    $[103] = t3;
    $[104] = t10;
  } else {
    t10 = $[104];
  }
  let t11;
  if ($[105] !== t) {
    t11 = t("home.pagination.prev");
    $[105] = t;
    $[106] = t11;
  } else {
    t11 = $[106];
  }
  let t12;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <svg viewBox="0 0 24 24" className={styles.pageNavIcon} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>;
    $[107] = t12;
  } else {
    t12 = $[107];
  }
  let t13;
  if ($[108] !== handlePagePrev || $[109] !== t11) {
    t13 = <button type="button" onClick={handlePagePrev} className={styles.pageNav} aria-label={t11}>{t12}</button>;
    $[108] = handlePagePrev;
    $[109] = t11;
    $[110] = t13;
  } else {
    t13 = $[110];
  }
  let t14;
  if ($[111] !== currentPage || $[112] !== pageNumbers) {
    t14 = pageNumbers.map(pageNumber => {
      const handleClick_1 = () => setPage(pageNumber);
      const isActive_0 = currentPage === pageNumber;
      return <button key={pageNumber} type="button" onClick={handleClick_1} className={joinClassNames(styles.pageNumber, isActive_0 && styles.pageNumberActive)}>{pageNumber}</button>;
    });
    $[111] = currentPage;
    $[112] = pageNumbers;
    $[113] = t14;
  } else {
    t14 = $[113];
  }
  let t15;
  if ($[114] !== t) {
    t15 = t("home.pagination.next");
    $[114] = t;
    $[115] = t15;
  } else {
    t15 = $[115];
  }
  let t16;
  if ($[116] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <svg viewBox="0 0 24 24" className={styles.pageNavIcon} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>;
    $[116] = t16;
  } else {
    t16 = $[116];
  }
  let t17;
  if ($[117] !== handlePageNext || $[118] !== t15) {
    t17 = <button type="button" onClick={handlePageNext} className={styles.pageNav} aria-label={t15}>{t16}</button>;
    $[117] = handlePageNext;
    $[118] = t15;
    $[119] = t17;
  } else {
    t17 = $[119];
  }
  let t18;
  if ($[120] !== t13 || $[121] !== t14 || $[122] !== t17) {
    t18 = <RevealOnScroll delayMs={120}><div className={styles.pagination}>{t13}{t14}{t17}</div></RevealOnScroll>;
    $[120] = t13;
    $[121] = t14;
    $[122] = t17;
    $[123] = t18;
  } else {
    t18 = $[123];
  }
  let t19;
  if ($[124] !== t10 || $[125] !== t18 || $[126] !== t4 || $[127] !== t5 || $[128] !== t6) {
    t19 = <section className={t4}>{t5}{t6}{t10}{t18}</section>;
    $[124] = t10;
    $[125] = t18;
    $[126] = t4;
    $[127] = t5;
    $[128] = t6;
    $[129] = t19;
  } else {
    t19 = $[129];
  }
  let t20;
  if ($[130] !== handleVideoClose || $[131] !== handleVideoNext || $[132] !== handleVideoPrev || $[133] !== locale || $[134] !== videoIdx || $[135] !== videos) {
    t20 = videoIdx !== null ? <VideoModal index={videoIdx} videos={videos} locale={locale} onClose={handleVideoClose} onPrev={handleVideoPrev} onNext={handleVideoNext} /> : null;
    $[130] = handleVideoClose;
    $[131] = handleVideoNext;
    $[132] = handleVideoPrev;
    $[133] = locale;
    $[134] = videoIdx;
    $[135] = videos;
    $[136] = t20;
  } else {
    t20 = $[136];
  }
  let t21;
  if ($[137] !== t19 || $[138] !== t20 || $[139] !== t7 || $[140] !== t8 || $[141] !== t9) {
    t21 = <div className={t7}>{t8}{t9}{t19}{t20}</div>;
    $[137] = t19;
    $[138] = t20;
    $[139] = t7;
    $[140] = t8;
    $[141] = t9;
    $[142] = t21;
  } else {
    t21 = $[142];
  }
  return t21;
}
function _temp5(chunks) {
  return <span className={styles.heroTitleHighlight}>{chunks}</span>;
}
function _temp4() {
  return <br />;
}
function _temp3(current_1) {
  return current_1 === null ? current_1 : previousVideoIndex(current_1);
}
function _temp2(current) {
  return Math.max(1, current - 1);
}
function _temp(_, index) {
  return index + 1;
}
type VideoModalProps = {
  index: number;
  videos: BlogVideo[];
  locale: string;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
};

// ui-v3 Dialog 미사용 사유: DialogContent 가 포커스 스코프이자 스타일드 카드라서,
// 카드 밖(오버레이 위)에 떠 있는 이전/다음 내비 버튼 레이아웃(SCSS)이 깨진다.
// 대신 dialog 시맨틱·Escape·포커스 이동/복원·스크롤락을 직접 처리한다.
function VideoModal(t0) {
  const $ = _c(67);
  const {
    index,
    videos,
    locale,
    onClose,
    onPrev,
    onNext
  } = t0;
  const t = useTranslations("blog");
  const titleId = useId();
  const closeButtonRef = useRef(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const previouslyFocused = document.activeElement;
      closeButtonRef.current?.focus();
      return () => {
        if (previouslyFocused instanceof HTMLElement) {
          previouslyFocused.focus();
        }
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[2] !== onClose) {
    t3 = () => {
      const handleKeyDown = event => {
        if (event.key === "Escape") {
          onClose();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t4 = [onClose];
    $[2] = onClose;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  useEffect(_temp7, t5);
  const video = videos[index];
  if (!video) {
    return null;
  }
  const videoTitle = video.title;
  const canPrev = index > 0;
  const canNext = index < videos.length - 1;
  const stopPropagation = _temp8;
  let t6;
  if ($[6] !== canPrev || $[7] !== onPrev) {
    t6 = event_1 => {
      event_1.stopPropagation();
      if (canPrev) {
        onPrev();
      }
    };
    $[6] = canPrev;
    $[7] = onPrev;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const handlePrev = t6;
  let t7;
  if ($[9] !== canNext || $[10] !== onNext) {
    t7 = event_2 => {
      event_2.stopPropagation();
      if (canNext) {
        onNext();
      }
    };
    $[9] = canNext;
    $[10] = onNext;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const handleNext = t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = joinClassNames(styles.modalNav, styles.modalNavPrev);
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const t9 = !canPrev;
  let t10;
  if ($[13] !== t) {
    t10 = t("home.videoModal.prev");
    $[13] = t;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <svg viewBox="0 0 24 24" className={styles.modalNavIcon} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== handlePrev || $[17] !== t10 || $[18] !== t9) {
    t12 = <button type="button" className={t8} onClick={handlePrev} hidden={t9} aria-label={t10}>{t11}</button>;
    $[16] = handlePrev;
    $[17] = t10;
    $[18] = t9;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== video.category) {
    t13 = <p className={styles.modalCategory}>{video.category}</p>;
    $[20] = video.category;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== titleId || $[23] !== videoTitle) {
    t14 = <h3 id={titleId} className={styles.modalTitle}>{videoTitle}</h3>;
    $[22] = titleId;
    $[23] = videoTitle;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t13 || $[26] !== t14) {
    t15 = <div>{t13}{t14}</div>;
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== locale || $[29] !== video.guideHref) {
    t16 = resolvePublicHref(locale, video.guideHref) ?? "#";
    $[28] = locale;
    $[29] = video.guideHref;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t) {
    t17 = t("home.videoModal.guideLink");
    $[31] = t;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t16 || $[34] !== t17) {
    t18 = <Link href={t16} className={styles.modalGuideLink}>{t17}</Link>;
    $[33] = t16;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t) {
    t19 = t("home.videoModal.close");
    $[36] = t;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <svg viewBox="0 0 24 24" className={styles.modalCloseIcon} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== onClose || $[40] !== t19) {
    t21 = <button ref={closeButtonRef} type="button" onClick={onClose} className={styles.modalClose} aria-label={t19}>{t20}</button>;
    $[39] = onClose;
    $[40] = t19;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t15 || $[43] !== t18 || $[44] !== t21) {
    t22 = <div className={styles.modalHeader}>{t15}{t18}{t21}</div>;
    $[42] = t15;
    $[43] = t18;
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t || $[47] !== video.youtubeId || $[48] !== videoTitle) {
    t23 = <div className={styles.modalPreview}>{video.youtubeId ? <iframe className={styles.modalPreviewIframe} src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?autoplay=1`} title={videoTitle} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen={true} loading="lazy" referrerPolicy="strict-origin-when-cross-origin" /> : <div className={styles.modalPreviewInner}>{t("home.videoModal.comingSoon")}</div>}</div>;
    $[46] = t;
    $[47] = video.youtubeId;
    $[48] = videoTitle;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t22 || $[51] !== t23) {
    t24 = <div className={styles.modalCard} onClick={stopPropagation}>{t22}{t23}</div>;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = joinClassNames(styles.modalNav, styles.modalNavNext);
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  const t26 = !canNext;
  let t27;
  if ($[54] !== t) {
    t27 = t("home.videoModal.next");
    $[54] = t;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <svg viewBox="0 0 24 24" className={styles.modalNavIcon} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  let t29;
  if ($[57] !== handleNext || $[58] !== t26 || $[59] !== t27) {
    t29 = <button type="button" className={t25} onClick={handleNext} hidden={t26} aria-label={t27}>{t28}</button>;
    $[57] = handleNext;
    $[58] = t26;
    $[59] = t27;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  let t30;
  if ($[61] !== onClose || $[62] !== t12 || $[63] !== t24 || $[64] !== t29 || $[65] !== titleId) {
    t30 = <div role="dialog" aria-modal="true" aria-labelledby={titleId} className={styles.modalOverlay} onClick={onClose}>{t12}{t24}{t29}</div>;
    $[61] = onClose;
    $[62] = t12;
    $[63] = t24;
    $[64] = t29;
    $[65] = titleId;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  return t30;
}
function _temp8(event_0) {
  event_0.stopPropagation();
}
function _temp7() {
  document.body.style.overflow = "hidden";
  return _temp6;
}
function _temp6() {
  document.body.style.overflow = "";
}